import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, CheckCircle2, Copy, Check, ExternalLink, Zap, Crown, Clock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { trackMcpPreviewOpen, trackUrlCopy, trackEvent } from "../analytics";

const SUCCESS_GREEN_BG = "#EAF3DE";
const SUCCESS_GREEN = "#3B6D11";
const SUCCESS_GREEN_TEXT = "#27500A";

// ── Mini copy button ────────────────────────────────────────────────────────
function MiniCopy({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-white/10 hover:bg-white/20 text-zinc-300 transition-colors"
    >
      {copied ? <Check className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ── What's next onboarding steps ────────────────────────────────────────────
const CLIENTS = [
  { id: "claude_desktop", label: "Claude Desktop" },
  { id: "claude_web",     label: "claude.ai" },
  { id: "cursor",         label: "Cursor" },
  { id: "cline",          label: "Cline" },
  { id: "chatgpt",        label: "ChatGPT" },
] as const;

type ClientId = typeof CLIENTS[number]["id"];

function OnboardingSteps({ mcpUrl }: { mcpUrl: string }) {
  const [client, setClient] = useState<ClientId>("claude_desktop");

  const configJson = JSON.stringify(
    { mcpServers: { "my-api": { url: mcpUrl } } },
    null, 2
  );

  const steps: Record<ClientId, { steps: string[]; code?: string; codeLabel?: string }> = {
    claude_desktop: {
      steps: [
        'Open Claude Desktop → Settings (top-right) → Developer → Edit Config',
        'Paste the JSON snippet below into your <code>claude_desktop_config.json</code>',
        'Save the file, then fully quit and relaunch Claude Desktop',
        'Ask Claude anything about your API — it now has access to all your tools!',
      ],
      code: configJson,
      codeLabel: "claude_desktop_config.json",
    },
    claude_web: {
      steps: [
        'Go to <a href="https://claude.ai" target="_blank" rel="noreferrer" class="underline">claude.ai</a> → Settings → Integrations → Add Integration',
        'Paste your MCP URL into the integration URL field',
        'Click Connect — Claude will verify the connection automatically',
        'Start a new chat and ask Claude to use your API tools!',
      ],
    },
    cursor: {
      steps: [
        'Open Cursor → Settings → Cursor Settings → MCP tab',
        'Click <strong>Add new MCP server</strong> → paste your MCP URL',
        'Fully quit and relaunch Cursor',
        'Open a chat with Agent mode — your API tools will appear automatically!',
      ],
      code: configJson,
      codeLabel: "cursor mcp config",
    },
    cline: {
      steps: [
        'Open VS Code → Cline panel → MCP Servers tab → click <strong>+</strong>',
        'Select <strong>Remote (SSE/HTTP)</strong> and paste your MCP URL',
        'Click Save — Cline connects instantly, no restart needed',
        'Open a Cline chat and ask it to use your API!',
      ],
    },
    chatgpt: {
      steps: [
        'Go to <a href="https://chatgpt.com" target="_blank" rel="noreferrer" class="underline">chatgpt.com</a> → Settings → Connected apps',
        'Click <strong>Add MCP server</strong> and paste your MCP URL',
        'ChatGPT will discover and list your API tools',
        'Open a new chat — your tools are available immediately!',
      ],
    },
  };

  const current = steps[client];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.4 }}
      className="mt-5"
    >
      {/* Client tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {CLIENTS.map(c => (
          <button
            key={c.id}
            type="button"
            onClick={() => { setClient(c.id); trackEvent("Onboarding", "client_selected", c.id); }}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              client === c.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Steps */}
      <div className="space-y-2.5 mb-4">
        {current.steps.map((step, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
              {i + 1}
            </span>
            <p
              className="text-xs text-foreground/80 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: step }}
            />
          </div>
        ))}
      </div>

      {/* Code snippet */}
      {current.code && (
        <div className="rounded-xl overflow-hidden border border-zinc-800 text-left">
          <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-900 border-b border-zinc-800">
            <span className="text-[10px] font-mono text-zinc-500">{current.codeLabel}</span>
            <MiniCopy text={current.code} />
          </div>
          <pre className="bg-zinc-950 text-zinc-200 text-[11px] leading-relaxed p-3 overflow-x-auto font-mono whitespace-pre-wrap">
            {current.code}
          </pre>
        </div>
      )}
    </motion.div>
  );
}

type ConvertActionProps = {
  onConvert: () => void;
  isConverting: boolean;
  lastConvertSuccess: boolean;
  mcpUrl?: string | null;
  isPro?: boolean;
  /** When false, the Generate button is disabled until credentials are filled in. Defaults to true. */
  credentialsReady?: boolean;
};

const ConvertAction = ({ onConvert, isConverting, lastConvertSuccess, mcpUrl, isPro = false, credentialsReady = true }: ConvertActionProps) => {
  const [copied, setCopied] = useState(false);

  const copyUrl = async () => {
    if (!mcpUrl) return;
    try {
      await navigator.clipboard.writeText(mcpUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackUrlCopy(mcpUrl);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="py-10 px-6">
      <div className="container max-w-2xl mx-auto">

        {/* ── Generate button (hidden after success) ── */}
        {!lastConvertSuccess && (
          <div className="flex flex-col items-center gap-2">
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={!isConverting && credentialsReady ? { scale: 1.03 } : undefined}
              whileTap={!isConverting && credentialsReady ? { scale: 0.97 } : undefined}
              disabled={isConverting || !credentialsReady}
              onClick={onConvert}
              className="inline-flex items-center justify-center gap-2.5 px-10 py-4 rounded-2xl btn-neu-primary text-base transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConverting ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Zap className="w-5 h-5" />
              )}
              {isConverting ? "Generating MCP Server…" : "Generate & Host MCP Server"}
            </motion.button>
            {!credentialsReady && !isConverting && (
              <p className="text-xs text-muted-foreground">
                Enter your API base URL and credentials above to continue
              </p>
            )}
          </div>
        )}

        {/* Re-generate after success */}
        {lastConvertSuccess && (
          <div className="flex justify-center mb-5">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              disabled={isConverting}
              onClick={onConvert}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border bg-background hover:bg-accent text-sm font-semibold text-muted-foreground transition-all disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${isConverting ? "animate-spin" : ""}`} />
              {isConverting ? "Re-generating…" : "Re-generate"}
            </motion.button>
          </div>
        )}

        {/* ── Success card ── */}
        <AnimatePresence>
          {lastConvertSuccess && (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              className="glass-strong rounded-2xl shadow-premium"
            >
              <div className="p-7">
                {/* Icon + heading */}
                <div className="flex items-center gap-3 mb-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 280, damping: 18, delay: 0.15 }}
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: SUCCESS_GREEN_BG }}
                  >
                    <CheckCircle2 className="w-5 h-5" style={{ color: SUCCESS_GREEN }} />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-bold text-foreground leading-tight">
                        MCP Server Ready!
                      </h3>
                      {isPro ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-blue-400 bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 text-[11px] font-semibold">
                          <Crown className="w-2.5 h-2.5 fill-current" />
                          Live · Never expires
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold"
                          style={{
                            backgroundColor: SUCCESS_GREEN_BG,
                            borderColor: SUCCESS_GREEN,
                            color: SUCCESS_GREEN_TEXT,
                          }}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0"
                            style={{ backgroundColor: SUCCESS_GREEN }}
                          />
                          Live · 7 days
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Hosted in the cloud · No local setup needed
                    </p>
                  </div>
                </div>

                {mcpUrl && (
                  <>
                    {/* URL pill */}
                    <div className="mt-5 flex items-center gap-2 bg-muted/50 dark:bg-muted/30 border border-border rounded-xl px-4 py-3">
                      <code className="flex-1 text-xs font-mono text-foreground break-all leading-relaxed">
                        {mcpUrl}
                      </code>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <motion.button
                          type="button"
                          onClick={copyUrl}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Copy URL"
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <AnimatePresence mode="wait" initial={false}>
                            {copied ? (
                              <motion.span key="c" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex">
                                <Check className="w-4 h-4" style={{ color: SUCCESS_GREEN }} />
                              </motion.span>
                            ) : (
                              <motion.span key="u" initial={{ scale: 0.6 }} animate={{ scale: 1 }} exit={{ scale: 0.6 }} className="flex">
                                <Copy className="w-4 h-4" />
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </motion.button>
                        <a
                          href={mcpUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open in new tab"
                          onClick={() => trackMcpPreviewOpen(mcpUrl)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>

                    {/* Upgrade nudge for free users */}
                    {!isPro && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="mt-4 flex items-center gap-3 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50"
                      >
                        <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                        <p className="text-xs text-amber-800 dark:text-amber-300 flex-1">
                          This MCP expires in <strong>7 days</strong>.
                        </p>
                        <Link
                          to="/pricing"
                          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold hover:opacity-90 transition"
                        >
                          <Crown className="w-3 h-3" />
                          Upgrade
                        </Link>
                      </motion.div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pre-conversion hint */}
        {!lastConvertSuccess && !isConverting && (
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-xs text-muted-foreground mt-4 text-center"
          >
            Hosts your MCP server instantly in the cloud — no local setup needed.
          </motion.p>
        )}
      </div>
    </section>
  );
};

export default ConvertAction;
